/**
 * THE dynamic-content contract, in one place — imported by BOTH the backend
 * (compile, materialize, agent tools, resolvers) and the app (canvas, email
 * editor). Keep this file dependency-free and browser-safe: constants and
 * pure string helpers only (no cheerio, no node APIs).
 *
 * The contract:
 * - A slot is an element in the email version HTML carrying
 *   `data-ht-dynamic-slot="<usageId>"`.
 * - Branch content is that element's OUTER HTML with the marker stripped —
 *   a single root element. Rendering (preview materialize, branch cards,
 *   compiled ESP switches) REPLACES the marked element wholesale and, where
 *   the marker must survive (previews), re-stamps it onto the content's
 *   root via `stampMarkerOnRootTag`.
 * - Empty content is legal and means "this audience sees nothing".
 * - Slots never nest: content must not contain the marker attribute.
 */

export const DYNAMIC_SLOT_ATTRIBUTE = "data-ht-dynamic-slot";

/**
 * The branch a selection resolves to for a slot: the selected branch when it
 * still exists, else the first branch in stored (waterfall priority) order —
 * both the default and the self-heal when a stale selection names a deleted
 * branch. The single definition of "which variant does this slot show", so
 * the preview's DOM materialize and the backend's splice materialize can
 * never disagree about what an export renders.
 */
export function effectiveSelection<Branch extends { id: string }>(
  slot: { id: string; branches: readonly Branch[] },
  selections: Record<string, string> | undefined,
): Branch | undefined {
  const selectedId = selections?.[slot.id];
  return (
    slot.branches.find((branch) => branch.id === selectedId) ?? slot.branches[0]
  );
}

/** Opening of a content string's root tag: optional leading whitespace, then
 * `<tagname`. Anchored — leading text or comments mean there is no stampable
 * root. NOTE: these two regexes recognize the same thing and must stay in
 * sync; they differ only in what they capture (the stampable prefix vs the
 * tag name). */
export const CONTENT_ROOT_TAG_RE = /^(\s*<[a-zA-Z][a-zA-Z0-9-]*)/;
const CONTENT_ROOT_TAG_NAME_RE = /^\s*<([a-zA-Z][a-zA-Z0-9-]*)/;

/** The content's root tag name (lowercase), or null when the content doesn't
 * start with an element. Sniffed without a parse — the parse itself needs
 * the tag to pick a valid context for table fragments (td/tr). */
export function contentRootTagName(content: string): string | null {
  return CONTENT_ROOT_TAG_NAME_RE.exec(content)?.[1]?.toLowerCase() ?? null;
}

/**
 * Insert the slot marker into the content's root opening tag, so a document
 * materialized from this content keeps the slot locatable. Returns "" for
 * empty content (the element is removed — that audience sees nothing) and
 * null when there is no root tag to stamp (callers reject; write boundaries
 * should have validated single-root content already).
 */
export function stampMarkerOnRootTag(
  content: string,
  slotId: string,
): string | null {
  if (content.trim() === "") return "";
  const match = CONTENT_ROOT_TAG_RE.exec(content);
  if (!match || match[1] === undefined) return null;
  const insertAt = match[1].length;
  return `${content.slice(0, insertAt)} ${DYNAMIC_SLOT_ATTRIBUTE}="${slotId}"${content.slice(insertAt)}`;
}
